import { useState } from "react";
import { Sparkles, Crown, Zap, Settings as SettingsIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/hooks/useSubscription";
import { openCustomerPortal } from "@/lib/stripe";
import { PaymentMethodPicker } from "@/components/PaymentMethodPicker";
import { toast } from "sonner";

// Replace these placeholders with real Stripe Price IDs from your Stripe dashboard.
const PRO_MONTHLY_PRICE_ID = "price_REPLACE_ME_PRO_MONTHLY";
const CREATOR_MONTHLY_PRICE_ID = "price_REPLACE_ME_CREATOR_MONTHLY";
// Replace with real PayPal plan IDs (created in PayPal dashboard → Pay & Get Paid → Subscriptions).
const PRO_MONTHLY_PAYPAL_PLAN_ID = "P-REPLACE_ME_PRO_MONTHLY";
const CREATOR_MONTHLY_PAYPAL_PLAN_ID = "P-REPLACE_ME_CREATOR_MONTHLY";

type Tier = {
  key: string;
  name: string;
  priceCents: number;
  stripePriceId: string;
  paypalPlanId: string;
  perks: string[];
  icon: typeof Crown;
};

const TIERS: Tier[] = [
  {
    key: "pro",
    name: "Pro",
    priceCents: 499,
    stripePriceId: PRO_MONTHLY_PRICE_ID,
    paypalPlanId: PRO_MONTHLY_PAYPAL_PLAN_ID,
    icon: Zap,
    perks: ["Unlimited gigs", "Priority support", "Pro badge"],
  },
  {
    key: "creator",
    name: "Creator",
    priceCents: 999,
    stripePriceId: CREATOR_MONTHLY_PRICE_ID,
    paypalPlanId: CREATOR_MONTHLY_PAYPAL_PLAN_ID,
    icon: Crown,
    perks: ["Everything in Pro", "Creator analytics", "Higher payout limits"],
  },
];

export const UpgradeToPro = () => {
  const { isPro, subscription_tier, current_period_end, loading, refresh } = useSubscription();
  const [picker, setPicker] = useState<Tier | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      await openCustomerPortal();
    } catch (e) {
      toast.error((e as Error).message || "Could not open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-primary/10 p-5">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold">Premium</h3>
        {isPro && (
          <Badge variant="secondary" className="ml-auto">
            {subscription_tier ?? "Active"}
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking subscription…
        </div>
      ) : isPro ? (
        <>
          <p className="text-xs text-muted-foreground mt-1">
            You're on the {subscription_tier ?? "Premium"} plan.
            {current_period_end && (
              <> Renews {new Date(current_period_end).toLocaleDateString()}.</>
            )}
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openPortal}
              disabled={portalLoading}
              className="flex-1"
            >
              {portalLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <SettingsIcon className="h-4 w-4 mr-1.5" />
                  Manage billing
                </>
              )}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={refresh}>
              Refresh
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mt-1">
            Unlock pro perks, higher limits, and exclusive features.
          </p>
          <div className="mt-3 space-y-2">
            {TIERS.map((tier) => {
              const Icon = tier.icon;
              return (
                <button
                  key={tier.key}
                  type="button"
                  onClick={() => setPicker(tier)}
                  className="w-full rounded-xl border border-border bg-card/60 p-3 text-left hover:border-primary transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-display font-bold text-sm">{tier.name}</span>
                        <span className="text-sm font-bold">
                          ${(tier.priceCents / 100).toFixed(2)}
                          <span className="text-xs text-muted-foreground font-normal">/mo</span>
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {tier.perks.join(" · ")}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {picker && (
        <PaymentMethodPicker
          open={!!picker}
          onOpenChange={(o) => !o && setPicker(null)}
          title={`Upgrade to ${picker.name}`}
          description={`$${(picker.priceCents / 100).toFixed(2)}/month`}
          amountCents={picker.priceCents}
          currency="USD"
          featureKey={`subscription_${picker.key}`}
          stripePriceId={picker.stripePriceId}
          stripeMode="subscription"
          paypalPlanId={picker.paypalPlanId.startsWith("P-REPLACE") ? undefined : picker.paypalPlanId}
          onSuccess={() => {
            toast.success("Redirecting to checkout…");
            setTimeout(refresh, 3000);
          }}
        />
      )}
    </div>
  );
};
